import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import cn from 'clsx';
import React, { useState, useEffect } from 'react';
import { DataReviewTableColumns } from '@components/admin/studies/data-review-tables/edit-columns';
import { useBoolean, useTranslation } from 'hooks';
import DeleteColumn from 'views/components/admin/studies/data-review-tables/delete-column';

import StudyDashboardItem from 'views/components/admin/study-dashboard-panel/study-dashboard-item';

import './edit-column-panel.scss';

interface IStudyDashboardPanel {
    panelItems: DataReviewTableColumns[];
    disabled: boolean;
    setPanelItems: (items: DataReviewTableColumns[]) => void;
    onOpenColumnDialog: (item?: DataReviewTableColumns) => void;
}

const EditColumnPanel = ({ panelItems, disabled, setPanelItems, onOpenColumnDialog }: IStudyDashboardPanel) => {
    const { t } = useTranslation();
    const showBorder = useBoolean(false);
    const deleteColumnOpen = useBoolean(false);

    const [itemToMove, setItemToMove] = useState<DataReviewTableColumns | null>(null);
    const [indexToRemove, setItemToRemove] = useState<number | null>(null);

    let timeout: ReturnType<typeof setTimeout>;
    let timeoutLong: ReturnType<typeof setTimeout>;

    useEffect(
        () => () => {
            clearTimeout(timeout);
            clearTimeout(timeoutLong);
        },
        [],
    );

    const moveItem = (oldIndex: number, newIndex: number) => {
        showBorder.setTrue();
        setItemToMove(panelItems[oldIndex]);
        timeout = setTimeout(() => {
            const array = [...panelItems];
            array[oldIndex].index = newIndex;
            array[newIndex].index = oldIndex;
            if (newIndex >= array.length) newIndex = array.length - 1;
            array.splice(newIndex, 0, array.splice(oldIndex, 1)[0]);
            setPanelItems(array);
        }, 600); // For creating effect of delay by design
        timeoutLong = setTimeout(() => {
            showBorder.setFalse();
            setItemToMove(null);
        }, 1200); // For creating effect of delay by design
    };

    const removeItem = (_: DataReviewTableColumns | null, index: number | null, canBeRemoved?: boolean) => {
        if (!canBeRemoved) {
            setItemToRemove(index);
            deleteColumnOpen.setTrue();
        } else if (index !== null) {
            const array = [...panelItems];
            const secondArray = array.splice(index + 1);
            array.splice(index, 1);
            array.push(...secondArray);
            setPanelItems(array);
        }
    };

    return (
        <>
            {deleteColumnOpen.value && (
                <DeleteColumn
                    onClose={(action) => {
                        if (action === 'delete') removeItem(null, indexToRemove, true);
                        setItemToRemove(null);
                        deleteColumnOpen.setFalse();
                    }}
                />
            )}
            <div className="dashboard-panel panel-column">
                <div className="header-panel panel-column">
                    <div className="left">
                        <div className="item-number">{panelItems.length}</div>
                        <div className="title heading-column">{t('admin:columnHeading')}</div>
                        <div className="title col-type border-left">{t('admin:columnType')}</div>
                        <div className="title col-data border-left">{t('admin:columnData')}</div>
                    </div>
                    <div className="right panel-column">
                        <button
                            type="button"
                            className={cn('add-item edit-panel-item k-border-none', { disabled })}
                            onClick={() => {
                                if (!disabled) onOpenColumnDialog();
                            }}
                        >
                            <FontAwesomeIcon icon={['far', 'layer-plus']} className="icon" />
                        </button>
                    </div>
                </div>
                {!!panelItems?.length &&
                    panelItems.map((item, index) => (
                        <StudyDashboardItem
                            key={item.id}
                            panel={item}
                            index={index}
                            first={index === 0}
                            last={index === panelItems.length - 1}
                            selected={false}
                            disabled={disabled}
                            moveItem={moveItem}
                            removeItem={removeItem}
                            hasChild={false}
                            showBorder={showBorder.value && itemToMove?.id === item.id}
                            fieldsToShow={[
                                {
                                    field: 'heading',
                                    width: 'calc(40% - 2px)',
                                },
                                {
                                    field: '_type',
                                    width: 'calc(20% + 2px)',
                                },
                                {
                                    field: '_dataString',
                                    width: 'calc(40% + 1px)',
                                },
                            ]}
                            actions={[
                                {
                                    label: 'admin:editColumn',
                                    icon: ['fal', 'edit'],
                                    onClick: () => onOpenColumnDialog(item),
                                    enabled: !disabled,
                                },
                                {
                                    label: 'admin:deleteColumn',
                                    icon: ['fal', 'trash'],
                                    onClick: null,
                                    enabled: !disabled,
                                },
                            ]}
                        />
                    ))}
            </div>
        </>
    );
};

export default EditColumnPanel;
